import React, { useState } from "react";

// Mui
import { IconButton, Badge, Popover, Box, Typography, Tooltip, SelectChangeEvent } from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Hooks
import { useIntl } from "react-intl";

// Icons
import icons from "enums/icons";

// Components
import CustomIcon from "components/CustomIcon";
import { InfoWithIcon, UserRoleSelect } from "pages/ClientHub/components";

// Types
import { TUserRole } from "types";
import { TConvertedUser } from "pages/UserManagement/adaptares/converter";

const ArchivedUsers = ({
    users,
    userFunctions,
    adminsCount,
    onUnarchiveUser,
    onUpdateRole,
}: {
    users: TConvertedUser[];
    userFunctions: TUserRole[];
    adminsCount: number;
    onUnarchiveUser: (userData: TConvertedUser) => void;
    onUpdateRole: (event: SelectChangeEvent, userId: number, isArchivedUser?: boolean) => void;
}) => {
    const intl = useIntl();

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const usersCount = users.length;
    const open = Boolean(anchorEl);

    const handleOpenArchivedUsers = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleToggleExpand = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        console.log("here");
    };

    const handleUnarchiveUser = (user: any) => {
        if (usersCount === 1) {
            handleClose();
        }
        onUnarchiveUser(user);
    };

    return (
        <>
            <IconButton disabled={!usersCount} sx={{ ml: 1.5 }} onClick={handleOpenArchivedUsers}>
                <Badge
                    badgeContent={usersCount}
                    sx={(theme) => ({
                        "& .MuiBadge-badge": {
                            backgroundColor: theme.palette.info.main,
                            color: theme.palette.common.white,
                        },
                    })}
                >
                    <DeleteOutlinedIcon />
                </Badge>
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                sx={{
                    border: `1px solid #F9FAFB`,
                    boxShadow: `0px 4px 6px 0px #0000000D, 0px 10px 15px 0px #0000001A`,
                }}
            >
                <Box sx={{ p: 2, width: "100%", maxWidth: 500 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 600, color: "text.primary" }}>
                            {intl.formatMessage({ id: "userManagement.archiveUser.popover.title" })}
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <CustomIcon icon={icons.close} sx={{ fontSize: 12 }} />
                        </IconButton>
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary", mb: 2 }}>
                        {intl.formatMessage({ id: "userManagement.archiveUser.popover.description" })}
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                        {users.map((user) => {
                            const hasFunction = !user.roles.map((role) => role.name).includes("placeholder");
                            return (
                                <Box
                                    key={user.id}
                                    sx={(theme) => ({
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        paddingTop: 1,
                                        paddingBottom: 1,
                                        marginBotto: 1,
                                        borderBottom: `1px solid ${theme.palette.neutrals.light}`,
                                    })}
                                >
                                    <InfoWithIcon
                                        size="small"
                                        startSlot={
                                            <IconButton onClick={(event) => handleToggleExpand(event)}>
                                                <InfoIcon
                                                    sx={(theme) => ({
                                                        fontSize: 18,
                                                        color: theme.palette.info.main,
                                                    })}
                                                />
                                            </IconButton>
                                        }
                                        entityName={user.name}
                                        primary={user.job_title}
                                    />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Box width={170}>
                                            <UserRoleSelect
                                                data={user}
                                                onRoleSelection={(event) => onUpdateRole(event, user.id, true)}
                                                adminsCount={adminsCount}
                                                list={userFunctions}
                                            />
                                        </Box>
                                        <Tooltip
                                            arrow
                                            disableHoverListener={hasFunction}
                                            disableFocusListener={hasFunction}
                                            placement="bottom-start"
                                            title={intl.formatMessage({
                                                id: "userManagement.archiveUser.tooltip.addUser",
                                            })}
                                        >
                                            <span>
                                                <IconButton
                                                    disabled={!hasFunction}
                                                    onClick={() => handleUnarchiveUser(user)}
                                                >
                                                    <CheckCircleIcon
                                                        sx={(theme) => ({
                                                            fontSize: 24,
                                                            color: theme.palette.success.main,
                                                            opacity: !hasFunction ? 0.5 : 1,
                                                        })}
                                                    />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default ArchivedUsers;
